import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import type { SupportTicket } from '@/lib/supportTickets';

/**
 * useMyTickets — what the current user has reported.
 * useAllTickets — admin only, every ticket in the system.
 *
 * Both subscribe to realtime so status changes show up instantly
 * without a refresh.
 */

export function useMyTickets() {
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) { setTickets([]); setLoading(false); return; }
    const { data } = await supabase
      .from('support_tickets')
      .select('*')
      .eq('user_id', user.id)
      .order('updated_at', { ascending: false });
    setTickets((data || []) as any);
    setLoading(false);
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  useEffect(() => {
    let cancelled = false;
    let channel: any = null;
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (cancelled || !user) return;
      channel = supabase
        .channel(`my-tickets-${user.id}`)
        .on('postgres_changes',
          { event: '*', schema: 'public', table: 'support_tickets', filter: `user_id=eq.${user.id}` },
          () => fetch())
        .on('postgres_changes',
          { event: 'INSERT', schema: 'public', table: 'support_ticket_messages' },
          async (payload: any) => {
            const msg = payload.new;
            // Solo nos interesan mensajes en tickets del usuario actual
            // que NO escribió él mismo (son respuestas del admin) y
            // que no son internas (RLS ya filtra pero por si acaso).
            if (!msg || msg.is_internal || msg.author_id === user.id) return;
            const { data: t } = await supabase
              .from('support_tickets')
              .select('ticket_number, title, user_id')
              .eq('id', msg.ticket_id)
              .maybeSingle();
            if (!t || t.user_id !== user.id) return;
            // Refrescar lista + mostrar toast
            fetch();
            toast.message(`💬 Tienes una respuesta en ${t.ticket_number}`, {
              description: t.title.slice(0, 80),
              duration: 6000,
            });
          })
        .subscribe();
    })();
    return () => {
      cancelled = true;
      if (channel) supabase.removeChannel(channel);
    };
  }, [fetch]);

  // Refetch al volver a la pestaña — cubre cuando el cliente estaba
  // en background y la suscripción realtime se pudo haber perdido.
  useEffect(() => {
    const onFocus = () => fetch();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') fetch();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [fetch]);

  return { tickets, loading, refetch: fetch };
}

export interface AdminTicketRow extends SupportTicket {
  user_name: string;
  unread_for_admin: boolean;
}

export function useAllTickets() {
  const [tickets, setTickets] = useState<AdminTicketRow[]>([]);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    // Orden: tickets ABIERTOS arriba (status != resolved), luego por
    // prioridad (P1 → P2 → P3 → P4), luego por fecha (más reciente
    // primero). Los resueltos van al final ordenados por fecha.
    //
    // Implementamos el sort en JS porque Supabase no permite expresiones
    // condicionales en .order(). El query trae todo y reordenamos.
    const { data } = await supabase
      .from('support_tickets')
      .select('*')
      .order('updated_at', { ascending: false });

    if (!data || data.length === 0) { setTickets([]); setLoading(false); return; }

    // Sort: open tickets by priority then date, resolved at bottom
    const priorityRank: Record<string, number> = { P1: 0, P2: 1, P3: 2, P4: 3 };
    const statusRank = (s: string) => s === 'resolved' ? 1 : 0;
    (data as any[]).sort((a, b) => {
      const sa = statusRank(a.status);
      const sb = statusRank(b.status);
      if (sa !== sb) return sa - sb; // abiertos primero
      const pa = priorityRank[a.priority] ?? 2;
      const pb = priorityRank[b.priority] ?? 2;
      if (pa !== pb) return pa - pb; // P1 antes que P4
      return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
    });

    const userIds = Array.from(new Set(data.map((t: any) => t.user_id)));
    const [{ data: ob }, { data: pf }] = await Promise.all([
      supabase.from('client_onboarding_profiles').select('user_id, full_name').in('user_id', userIds),
      supabase.from('profiles').select('id, display_name').in('id', userIds),
    ]);
    const nameMap = new Map<string, string>();
    (ob || []).forEach((o: any) => { if (o.full_name) nameMap.set(o.user_id, o.full_name); });
    (pf || []).forEach((p: any) => {
      if (!nameMap.has(p.id) && p.display_name) nameMap.set(p.id, p.display_name);
    });

    setTickets((data as any[]).map((t: any) => ({
      ...t,
      user_name: nameMap.get(t.user_id) || t.context_user_email || 'Usuario',
      // unread_for_admin: open tickets older than 1 minute the admin
      // hasn't replied to. Cheap heuristic; a more robust impl would
      // track last_admin_view per ticket.
      unread_for_admin: t.status === 'open',
    })));
    setLoading(false);
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  // Tracking del user actual (admin) para detectar mensajes
  // ENTRANTES (escritos por otros) y mostrar toast.
  const currentUserIdRef = useRef<string | null>(null);
  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      currentUserIdRef.current = user?.id || null;
    });
  }, []);

  useEffect(() => {
    const ch = supabase
      .channel('all-tickets-admin')
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'support_tickets' },
        () => fetch())
      .on('postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'support_ticket_messages' },
        async (payload: any) => {
          const msg = payload.new;
          // Refrescar lista (para que el ticket se reordene/marque)
          fetch();
          // Toast in-app si el mensaje NO lo escribió el admin actual
          // y no es nota interna (las internas son ruido para todos
          // los admins que no las escribieron — aún así son admin-only)
          if (msg && !msg.is_internal && msg.author_id !== currentUserIdRef.current) {
            // Necesitamos el ticket_number para el toast — fetch corto
            const { data: t } = await supabase
              .from('support_tickets')
              .select('ticket_number, title')
              .eq('id', msg.ticket_id)
              .maybeSingle();
            if (t) {
              toast.message(`💬 Nueva respuesta en ${t.ticket_number}`, {
                description: t.title.slice(0, 80),
                duration: 6000,
              });
            }
          }
        })
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [fetch]);

  // Refetch cuando la pestaña/ventana vuelve a tener foco. Esto
  // cubre el caso en que el admin estuvo en otra app/tab y al
  // regresar quiere ver el estado más reciente — incluso si la
  // suscripción realtime se perdió o llegó algo mientras estaba
  // backgrounded.
  useEffect(() => {
    const onFocus = () => fetch();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') fetch();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [fetch]);

  return { tickets, loading, refetch: fetch };
}
